/**
 * A folder-style card: a tinted icon tile and the name (with an optional line
 * under it) on top; a count on the left and a figure on the right along the
 * bottom. Used for clients on Client Documents / Clients and for a client's
 * folders, so every "place you open" looks the same.
 */

import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export function FolderCard({
	icon,
	title,
	meta,
	count,
	figure,
	onOpen,
	href,
	component,
}: {
	icon: ReactNode;
	title: ReactNode;
	/** One line under the title (code · GSTIN). */
	meta?: ReactNode;
	/** Bottom-left: "8 files". */
	count: ReactNode;
	/** Bottom-right: a size or a second count. */
	figure?: ReactNode;
	/** Either a click handler… */
	onOpen?: () => void;
	/** …or a link: pass the router `Link` as `component` and the path as `href`. */
	href?: string;
	component?: React.ElementType;
}) {
	const link = component && href ? { component, to: href } : {};
	return (
		<Box
			{...link}
			role={onOpen ? 'button' : undefined}
			tabIndex={onOpen ? 0 : undefined}
			onClick={onOpen}
			onKeyDown={
				onOpen
					? (event: React.KeyboardEvent) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onOpen();
							}
						}
					: undefined
			}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				gap: 'var(--s3)',
				minWidth: 0,
				minHeight: 96,
				p: '12px 14px',
				bgcolor: 'var(--surface)',
				border: '1px solid var(--line)',
				borderRadius: '10px',
				boxShadow: 'var(--e1)',
				color: 'inherit',
				textDecoration: 'none',
				cursor: 'pointer',
				transition:
					'border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease)',
				'&:hover': {
					borderColor: 'var(--accent-border)',
					boxShadow: 'var(--e2)',
				},
				'&:hover .go': {
					color: 'var(--accent)',
					transform: 'translateX(2px)',
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: '10px',
					minWidth: 0,
				}}
			>
				<Box
					aria-hidden="true"
					sx={{
						flex: '0 0 32px',
						height: 32,
						borderRadius: '8px',
						display: 'grid',
						placeItems: 'center',
						bgcolor: 'var(--accent-surface)',
						color: 'var(--accent)',
						fontSize: 'var(--fs-small)',
						fontWeight: 700,
						'& svg': { fontSize: 18 },
					}}
				>
					{icon}
				</Box>
				<Box sx={{ minWidth: 0, flex: 1, pt: '1px' }}>
					<Typography
						noWrap
						sx={{
							fontSize: 'var(--fs-section)',
							fontWeight: 600,
							color: 'var(--ink)',
						}}
					>
						{title}
					</Typography>
					{meta && (
						<Typography
							noWrap
							sx={{
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							{meta}
						</Typography>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 'var(--s2)',
					fontSize: 'var(--fs-small)',
					color: 'var(--ink-3)',
				}}
			>
				<Typography component="span" sx={{ fontSize: 'inherit' }}>
					{count}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{figure && (
						<Typography
							component="span"
							sx={{ fontSize: 'inherit' }}
						>
							{figure}
						</Typography>
					)}
					<ChevronRightIcon
						className="go"
						aria-hidden="true"
						sx={{
							fontSize: 18,
							color: 'var(--ink-3)',
							transition: 'transform var(--dur-fast) var(--ease)',
						}}
					/>
				</Box>
			</Box>
		</Box>
	);
}
